<template>
  <component
    :is="embedded ? 'div' : 'v-card'"
    :class="rootClass"
  >
    <div class="d-flex align-center">
      <v-btn
        v-if="!embedded"
        class="mr-5"
        title="Back to ToxPrints Home"
        @click="$router.push('/toxprints')"
        v-b-tooltip
        aria-label="Back to ToxPrints Home"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>

      <AssayViewModeToggle
        v-if="!embedded"
        :assay-view="this"
      />

      <v-chip
        v-if="assay && assay.name"
        label
        outlined
        title="Assay Name"
        v-b-tooltip
      >
        {{ assay.name }}
      </v-chip>

      <v-spacer />

      <AssayViewControls
        v-if="!embedded"
        :assay-view="{
          chemicalsCount: chemicals ? chemicals.length : 0,
          view,
          hasActivities,
          hasEnriched,
          showOtherMetrics,
          showEnrichedOnly,
          showStructures,
          showEmptyRows: !hideChemicalsWithNoToxprints,
          showNames,
          showToxprintStructures,
          showImages,
          numberOfEmptyChemicals,
          pending,
          downloading,
          isAuthenticated,
          toxprintsCategories,
          assay,
          name,
          toxprintsUrl: TOXPRINTS_URL,
          canManageAcl: Boolean(assay.id && canACL(assay)),
          canDelete: canDelete(assay),
        }"
        @update:show-other-metrics="showOtherMetrics = $event"
        @update:show-enriched-only="showEnrichedOnly = $event"
        @update:show-structures="showStructures = $event"
        @update:show-empty-rows="hideChemicalsWithNoToxprints = !$event"
        @update:show-names="showNames = $event"
        @update:show-toxprint-structures="showToxprintStructures = $event"
        @update:show-images="showImages = $event"
        @export="exportFile($event)"
        @assay-created="onAssayCreated($event)"
        @assay-updated="onAssayUpdated($event)"
        @acl-updated="onAclUpdated($event)"
        @reset="onReset"
      />
    </div>

    <v-progress-linear
      v-if="pending"
      indeterminate
    />



    <ChemicalsToxprints
      v-if="view === 0 && chemicals"
      :chemicals="chemicals"
      :hide-empty="hideChemicalsWithNoToxprints"
      :labels="labels"
      :selected-chemical-key="selectedChemicalKey"
      :show-names="showNames"
      :current-page.sync="recordsCurrentPage"
      :items-per-page.sync="recordsPerPage"
      :profile="assayProfile"
      @navigate="jumpTo"
      @chemicalSelected="onChemicalSelected"
    />

    <ToxprintsChemicals
      v-if="view === 1 && chemicals && metrics"
      :items="metrics"
      :chemicals="chemicals"
      :labels="labels"
      :profile="assayProfile"
      :metrics="toxprintsMetrics"
      :selected-chemical-key="selectedChemicalKey"
      :show-names="showNames"
      :show-toxprint-structures="showToxprintStructures"
      :show-other-metrics="showOtherMetrics"
      :show-structures="showStructures"
      :show-enriched-only="showEnrichedOnly"
      :current-page.sync="toxprintsCurrentPage"
      :items-per-page.sync="toxprintsPerPage"
      @navigate="jumpTo"
    />

    <template v-if="hasChemicalDetailsView">
      <ToxprintsCategories
        v-if="view === 2 && chemical"
        :chemical="chemical"
        :show-names="showNames"
        :show-toxprint-structures="showToxprintStructures"
        @navigate="jumpTo"
      />

      <ChemicalsCategories
        v-if="view === 3 && chemicals"
        :chemicals="chemicals"
        :selected-chemical-key="selectedChemicalKey"
        :show-images="showImages"
        @chemicalClicked="onChemicalSelected"
      />
    </template>
  </component>
</template>

<script>
import { mapGetters } from 'vuex'
import { download, getDatedFilename, TOXPRINTS_URL, getErrorMessage } from '@/main'
import { showErrorToast } from '@/utils/toast'
import ChemicalsToxprints from '@/components/toxprints/ChemicalsToxprints'
import ToxprintsChemicals from '@/components/toxprints/ToxprintsChemicals'
import ToxprintsCategories from '@/components/toxprints/ToxprintsCategories'
import ChemicalsCategories from '@/components/toxprints/ChemicalsCategories'
import AuthBase from '@/components/auth/AuthBase'
import AssayViewControls from '@/components/toxprints/AssayViewControls.vue'
import AssayViewModeToggle from '@/components/toxprints/AssayViewModeToggle.vue'
import {
  defaultToxprintsAssayViewPreferences,
  getToxprintsAssayViewPreference,
  setToxprintsAssayViewPreference,
} from '@/components/toxprints/toxprintsPreferences'

export default {
  mixins: [AuthBase],

  components: {
    ToxprintsChemicals,
    ChemicalsToxprints,
    ToxprintsCategories,
    ChemicalsCategories,
    AssayViewControls,
    AssayViewModeToggle,
  },

  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      pending: false,
      downloading: false,
      recordsCurrentPage: 1,
      toxprintsCurrentPage: 1,
      loadedAssay: null,
      lastScrolledTargetKey: null,
      highlightedNavigationRow: null,
      clearNavigationHighlightTimeoutId: null,
    }
  },

  computed: {
    ...mapGetters([
      'isAuthenticated',
      'getCheckedStructures',
      'toxprintsResult',
      'toxprintsLabels',
      'toxprintsMetrics',
      'toxprintsCategories',
      'toxprintsSelectedChemical',
      'toxprintsDefaultProfile',
    ]),
    showNames: {
      get() {
        return this.getPersistedPreference('showNames', defaultToxprintsAssayViewPreferences.showNames)
      },
      set(value) {
        this.setPersistedPreference('showNames', Boolean(value))
      },
    },
    showStructures: {
      get() {
        return true
      },
      set(value) {
        this.setPersistedPreference('showStructures', true)
      },
    },
    showOtherMetrics: {
      get() {
        return this.getPersistedPreference('showOtherMetrics', defaultToxprintsAssayViewPreferences.showOtherMetrics)
      },
      set(value) {
        this.setPersistedPreference('showOtherMetrics', Boolean(value))
      },
    },
    showEnrichedOnly: {
      get() {
        return this.getPersistedPreference('showEnrichedOnly', this.hasEnriched)
      },
      set(value) {
        this.setPersistedPreference('showEnrichedOnly', Boolean(value))
      },
    },
    showToxprintStructures: {
      get() {
        return this.getPersistedPreference(
          'showToxprintStructures',
          defaultToxprintsAssayViewPreferences.showToxprintStructures,
        )
      },
      set(value) {
        this.setPersistedPreference('showToxprintStructures', Boolean(value))
      },
    },
    showImages: {
      get() {
        return this.getPersistedPreference('showImages', defaultToxprintsAssayViewPreferences.showImages)
      },
      set(value) {
        this.setPersistedPreference('showImages', Boolean(value))
      },
    },
    recordsPerPage: {
      get() {
        return this.getPersistedPreference('recordsPerPage', defaultToxprintsAssayViewPreferences.recordsPerPage)
      },
      set(value) {
        this.setPersistedPreference('recordsPerPage', Number(value))
      },
    },
    toxprintsPerPage: {
      get() {
        return this.getPersistedPreference('toxprintsPerPage', defaultToxprintsAssayViewPreferences.toxprintsPerPage)
      },
      set(value) {
        this.setPersistedPreference('toxprintsPerPage', Number(value))
      },
    },
    hideChemicalsWithNoToxprints: {
      get() {
        return this.getPersistedPreference(
          'hideChemicalsWithNoToxprints',
          defaultToxprintsAssayViewPreferences.hideChemicalsWithNoToxprints,
        )
      },
      set(value) {
        this.setPersistedPreference('hideChemicalsWithNoToxprints', Boolean(value))
      },
    },
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    name() {
      return this.$route.params.name
    },
    hasActivities() {
      return this.chemicals && !!this.chemicals.find((r) => r.activity)
    },
    hasEnriched() {
      return this.metrics && !!this.metrics.find((r) => r.CTE)
    },
    chemicals() {
      return this.assay ? this.assay.chemicals : null
    },
    assayProfile() {
      return this.assay?.options?.profile || null
    },
    labels() {
      return this.assayProfile ? this.toxprintsLabels[this.assayProfile] : undefined
    },
    numberOfEmptyChemicals() {
      return this.chemicals.filter((chemical) => this.bits2n_labels(chemical.bits).length === 0).length
    },
    metrics() {
      return this.assay ? this.assay.metrics : null
    },
    filteredChemicals() {
      if (!this.chemicals) return []
      return this.chemicals.filter(
        (chemical) => !this.hideChemicalsWithNoToxprints || this.bits2n_labels(chemical.bits).length > 0,
      )
    },
    filteredMetrics() {
      if (!this.metrics) return []
      return this.showEnrichedOnly ? this.metrics.filter((item) => item.CTE) : this.metrics
    },
    assay: {
      get() {
        return this.name ? this.loadedAssay : this.toxprintsResult
      },
      set(val) {
        this.name ? (this.loadedAssay = val) : this.$store.commit('setToxprintsResult', val)
      },
    },
    view: {
      get() {
        return this.$route.query.view != null ? parseInt(this.$route.query.view) : 0
      },
      set(val) {
        this.$router.push(
          '/toxprints/' +
            (this.name ? `assays/${encodeURIComponent(this.name)}` : 'report') +
            (val ? `?view=${val}` : ''),
        )
      },
    },
    chemical: {
      get() {
        return this.toxprintsSelectedChemical
      },
      set(val) {
        this.$store.commit('setToxprintsSelectedChemical', val)
      },
    },
    selectedChemicalKey() {
      return this.getChemicalId(this.chemical)
    },
    hasChemicalDetailsView() {
      return Boolean(this.assayProfile) && this.toxprintsDefaultProfile === this.assayProfile
    },
    rootClass() {
      return this.embedded ? 'd-flex flex-column w-100 h-100 toxprints-embedded-panel' : 'ma-1'
    },
  },

  watch: {
    'name'() {
      this.getData()
    },
    '$route.fullPath'() {
      this.syncNavigationState()
    },
    'filteredChemicals'() {
      this.syncNavigationState()
    },
    'filteredMetrics'() {
      this.syncNavigationState()
    },
  },

  methods: {
    getPersistedPreference(key, fallback) {
      let value = getToxprintsAssayViewPreference(key)
      return value == null ? fallback : value
    },
    setPersistedPreference(key, value) {
      setToxprintsAssayViewPreference(key, value)
    },
    onAclUpdated(acl) {
      this.assay.acl = acl
    },
    jumpTo(tab, item_id) {
      this.navigateToView(tab, item_id)
    },
    onChemicalSelected(chemical) {
      this.chemical = chemical

      if (this.hasChemicalDetailsView) {
        this.view = 2
      }
    },
    onReset() {
      if (this.name) {
        this.$axios.delete(`${TOXPRINTS_URL}/assays/${this.assay.id}`).then(() => {
          if (this.assay.id === this.toxprintsResult.id) this.$store.commit('resetToxprintsResult')
          this.$router.push('/toxprints/assays')
        })
      } else {
        this.$store.commit('resetToxprintsResult')
        this.$router.push('/toxprints')
      }
    },
    onAssayCreated(assay) {
      this.assay = assay
      this.$router.push(`/toxprints/assays/${encodeURIComponent(assay.name)}`)
    },
    onAssayUpdated(assay) {
      this.assay = assay
    },
    buildViewLocation(tab, itemId = null) {
      let path = '/toxprints/' + (this.name ? `assays/${encodeURIComponent(this.name)}` : 'report')
      let query = {}

      if (tab) query.view = String(tab)
      if (itemId != null) {
        query.target = String(itemId)

        let page = this.getTargetPage(tab, itemId)
        if (page != null) query.page = String(page)
      }

      return { path, query }
    },
    getItemsPerPageForView(tab) {
      return tab === 1 ? this.toxprintsPerPage : this.recordsPerPage
    },
    getItemsForView(tab) {
      return tab === 1 ? this.filteredMetrics : this.filteredChemicals
    },
    getItemIdForView(tab, item) {
      return tab === 1 ? item.ordinal : this.getChemicalId(item)
    },
    getChemicalId(chemical) {
      return chemical?.id ?? chemical?.chemId ?? chemical?.sid ?? chemical?.key
    },
    getTargetPage(tab, itemId) {
      let itemsPerPage = this.getItemsPerPageForView(tab)
      if (itemsPerPage === -1) return 1

      let targetIndex = this.getItemsForView(tab).findIndex((item) => this.getItemIdForView(tab, item) === itemId)
      if (targetIndex === -1) return null

      return Math.floor(targetIndex / itemsPerPage) + 1
    },
    navigateToView(tab, itemId = null) {
      let location = this.buildViewLocation(tab, itemId)
      let target = this.$router.resolve(location).route.fullPath

      if (target !== this.$route.fullPath) {
        this.$router.push(location)
      } else {
        this.syncNavigationState()
      }
    },
    syncNavigationState() {
      let routePage = Number.parseInt(this.$route.query.page, 10)
      if (!Number.isNaN(routePage) && routePage > 0) {
        if (this.view === 0) this.recordsCurrentPage = routePage
        if (this.view === 1) this.toxprintsCurrentPage = routePage
      }

      let targetId = this.$route.query.target
      if (targetId == null) {
        this.lastScrolledTargetKey = null
        return
      }

      let scrollKey = `${this.view}:${targetId}:${routePage || 1}`
      if (scrollKey === this.lastScrolledTargetKey) return

      this.$nextTick(() => {
        let selector = `#a-${this.view}-${targetId}`
        let target = this.$el?.querySelector(selector)

        if (target) {
          this.lastScrolledTargetKey = scrollKey
          this.scrollTargetIntoView(target)
          this.flashNavigationTarget(target)
          this.$scrollTo(selector)
        }
      })
    },
    scrollTargetIntoView(target) {
      let row = target.closest('tr')
      let wrapper = target.closest('.v-data-table__wrapper')

      if (!row || !wrapper) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' })
        return
      }

      let stickyHeader = wrapper.querySelector('thead')
      let headerOffset = stickyHeader?.getBoundingClientRect().height || 0
      let extraOffset = 12
      let rowRect = row.getBoundingClientRect()
      let wrapperRect = wrapper.getBoundingClientRect()
      let nextScrollTop = wrapper.scrollTop + (rowRect.top - wrapperRect.top) - headerOffset - extraOffset

      wrapper.scrollTo({ top: Math.max(nextScrollTop, 0), behavior: 'auto' })
    },
    flashNavigationTarget(target) {
      let row = target.closest('tr')
      if (!row) return

      if (this.clearNavigationHighlightTimeoutId) {
        window.clearTimeout(this.clearNavigationHighlightTimeoutId)
        this.clearNavigationHighlightTimeoutId = null
      }

      if (this.highlightedNavigationRow && this.highlightedNavigationRow !== row) {
        this.highlightedNavigationRow.classList.remove('navigation-target-flash')
      }

      row.classList.remove('navigation-target-flash')
      void row.offsetWidth
      row.classList.add('navigation-target-flash')
      this.highlightedNavigationRow = row

      this.clearNavigationHighlightTimeoutId = window.setTimeout(() => {
        row.classList.remove('navigation-target-flash')
        if (this.highlightedNavigationRow === row) {
          this.highlightedNavigationRow = null
        }
        this.clearNavigationHighlightTimeoutId = null
      }, 2200)
    },
    exportFile(format) {
      format = format.toLowerCase()
      this.downloading = true
      this.$axios
        .post(
          `${TOXPRINTS_URL}/export`,
          {
            assay: this.assay,
            format: format.toUpperCase(),
            sheet: format === 'csv' ? this.view : null,
            nChemical: this.chemical ? this.chemicals.indexOf(this.chemical) : null,
          },
          { responseType: 'arraybuffer' },
        )
        .then((response) => {
          let filename = getDatedFilename('toxprints', format)
          download(filename, response.data, format)
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during export' })
        })
        .finally(() => {
          this.downloading = false
        })
    },
    getData() {
      if (this.name) {
        this.$axios.get(`${TOXPRINTS_URL}/assays/${encodeURIComponent(this.name)}`).then((response) => {
          this.assay = response.data
        })
      }
    },
    bits2n_labels(bits) {
      let n_labels = []
      if (bits && this.labels) {
        for (let i = 0; i < bits.length; i++) {
          if (bits[i]) n_labels.push([i, this.labels[i]])
        }
      }
      return n_labels
    },
  },

  mounted() {
    if (this.name) this.getData()
    this.syncNavigationState()
    if (this.embedded) this.$emit('controller-ready', this)
  },

  beforeDestroy() {
    if (this.clearNavigationHighlightTimeoutId) {
      window.clearTimeout(this.clearNavigationHighlightTimeoutId)
    }
    if (this.embedded) this.$emit('controller-ready', null)
  },
}
</script>

<style scoped>
.toxprints-embedded-panel {
  min-height: 0;
}

::v-deep tr.navigation-target-flash > td {
  animation: navigation-target-flash 2.2s ease-out;
}

@keyframes navigation-target-flash {
  0% {
    background-color: rgba(130, 177, 255, 0.45);
    box-shadow: inset 0 0 0 9999px rgba(130, 177, 255, 0.2);
  }

  100% {
    background-color: transparent;
    box-shadow: inset 0 0 0 9999px rgba(130, 177, 255, 0);
  }
}
</style>
